<script setup>
import ChildList from "./ChildList.vue";
import TagList from "./TagList.vue";
import {computed, onMounted, ref} from "vue";
import useConnector from "./ConnectorComposition";

const siteUrl = document.querySelector('meta[name="site-url"]').content;
const props = defineProps({
  post: {
    type: Object,
    required: true,
  },
  loginUserId: {
    type: Number,
    required: true,
  },
  isDetailMode: {
    type: Boolean,
    required: true,
  },
  empathy: {
    type: Boolean,
    required: true,
  },
  favorite: {
    type: Boolean,
    required: true,
  },
  violation: {
    type: Boolean,
    required: true,
  },
  isOpenMenu: {
    type: Boolean,
    required: true,
  },
  createdAt: {
    type: Date,
    required: true,
  },
  isNew: {
    type: Boolean,
    required: true,
  },
  decade: {
    type: Number,
    required: true,
  },
  contentParagraph: {
    type: Array,
    required: true,
  },
  empathyCount: {
    type: Number,
    required: true,
  },
  isMyPost: {
    type: Boolean,
    required: true,
  },
  isLogin: {
    type: Boolean,
    required: true,
  }
});

const emits = defineEmits([
  'toggle-favorite',
  'toggle-empathy',
  'toggle-violation',
  'show-publish-modal',
  'show-comment-setting-modal',
  'open-menu',
  'close-menu',
]);

const _connector = useConnector();

// 編集中の補足文
const supplementLocal = ref('');

// 確定した補足文
const supplementRemote = ref(props.post.supplement);

// 補足エディターが開いているかどうか
const isOpenSupplementEditor = ref(false);

// 補足が確認状態かどうか
const isConfirm = ref(false);

// 補足エディターのテキストエリア
const supplementTextarea = ref(null);

// 補足エディターのテキストエリアのスタイル
const textareaStyle = ref('');

// 一覧画面にて、本文に「もっと見る」を表示するか否か
const isShowMore = computed(() => {
  // 詳細画面の場合は常に全体表示
  if (props.isDetailMode) {
    return false;
  }

  // 改行は 49 文字としてカウントするため、改行の数を数える
  const lineCount = props.post.content.split("\n").length - 1;

  // 表示先がだいたい 49 文字を超過する場合は「もっと見る」を表示。
  return (props.post.content.length + (lineCount * 48)) > 49;
});
const lyOrder4or5 = computed(() => isShowMore.value ? 'lyOrder5' : 'lyOrder4');
const lyOrder5or6 = computed(() => isShowMore.value ? 'lyOrder6' : 'lyOrder5');
const lyOrder6or7 = computed(() => isShowMore.value ? 'lyOrder7' : 'lyOrder6');


// 補足の文字数を正確に数える
// https://qiita.com/ka215/items/d059a78e29adef3978b5#6-%E3%83%9E%E3%83%AB%E3%83%81%E3%83%90%E3%82%A4%E3%83%88%E6%96%87%E5%AD%97%E3%81%AB%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%9F%E6%96%87%E5%AD%97%E5%88%97%E5%87%A6%E7%90%86
const supplementLength = computed(() => {
  //指定された範囲にサロゲートペアが存在するかを boolean で返す
  //upper は 1 バイト目、lower に 2 バイト目を指定する
  //UTF の場合、通常は 2 バイトで一文字だが、サロゲートペアの場合は 4 バイトで一文字を表す
  const isSurrogatePair = (upper, lower) => 0xD800 <= upper && upper <= 0xDBFF && 0xDC00 <= lower && lower <= 0xDFFF;

  //最終的に返す文字数
  let result = 0;

  //サロゲートペアがあればそれを 2 文字ではなく 1 文字としてカウントしつつ文字数を返す
  for (let i = 0; i < supplementLocal.value.length; i++,result++) {
    //サロゲートペアでない文字は一文字とカウントする
    if ( ! isSurrogatePair(supplementLocal.value.charCodeAt(i), supplementLocal.value.length > (i + 1) ? supplementLocal.value.charCodeAt(i + 1) : 0)) {
      continue;
    }

    //サロゲートペアなら一文字ぶん多くカウントする
    i++;
  }

  //結果文字列の数を返す
  return result;
});

// 補足の文字数が 5 文字以上 1000 文字以下であるか
const validContentLength = computed(() => supplementLength.value >= 1 && supplementLength.value <= 1000);

//補足を改行ごとに配列化して返す
const supplementLocalParagraph = computed(() => supplementLocal.value.replace(/\r\n/g, "\n").split("\n"));
const supplementRemoteParagraph = computed(() => supplementRemote.value.replace(/\r\n/g, "\n").split("\n"));

// キャンペーンタグ
const campaignTags = computed(() => props.post.tags.filter(tag => tag.is_campaign));

// 補足文の高さを調節
const adjustSupplementHeight = () => {
  // テキストエリアが存在しない場合は何もしない
  if (supplementTextarea.value === null) {
    return;
  }

  // 補足文の高さを調節
  textareaStyle.value = 'height:auto';
  textareaStyle.value = 'height'+supplementTextarea.value.scrollHeight+'px';
};

/**
 * 補足の更新を試みる
 * @returns {Promise<{result: boolean, errors: ToRef<[]>}>}
 */
const setSupplement = async () => {
  //送信パラメータの作成
  const body = new FormData();
  body.append('supplement', supplementLocal.value);

  //送信を試みる
  const result = await _connector.connectPost(siteUrl+'place/set-supplement/'+props.post.id, body);

  // 成功したら設定済みのフラグをたて、補足文を更新する
  if (result !== null) {
    supplementRemote.value = supplementLocal.value;
    isOpenSupplementEditor.value = false;
  }
};

// 初期化
onMounted(() => {
  // 高さを調節
  adjustSupplementHeight();

  // 疑似セレクトボックス外の要素をクリックした際、疑似セレクトボックスを非表示にする、
  document.addEventListener('click', (event) => {
    if ( ! event.target.closest('.modSelectTrigger')) {
      emits('close-menu');
    }
  });

  // URL のクエリに ?supplement=1 があれば isOpenSupplementEditor を true にする
  if ((new URLSearchParams(window.location.search)).has('supplement')) {
    isOpenSupplementEditor.value = true;
  }
});
</script>

<template>
  <div class="lyMb16 pBackColorWhite pBorderRadius4px pAnimation pHoverShadow">
    <article class="lyColumn pFontBlack pageBoardWrapper">
      <h1 v-if="isDetailMode" class="lyOrder2 lyPd8 pFont16 pFontBold">{{post.title}}</h1>
      <h2 v-else class="lyOrder2 lyPd8 pFont16 pFontBold">{{post.title}}</h2>
      <p
        v-if="isNew"
        class="lyW32px"
      >
        <img
          :src="siteUrl+'images/new.svg'"
          width="32"
          height="32"
          alt="NEW"
        >
      </p>
      <div class="lyOrder0 lyPd8 pBorderBottom1pxSolidGray4">
        <div class="lyHorizontalBw">
          <div class="lyVerticalCntBreak lyMr16">
            <p class="lyMr4">
              <span
                v-if="post.user_link_id === ''"
                class="lyRelative lyZIndex3 pFontBold"
              >{{post.user_nickname}}</span>
              <a
                v-else
                :href="siteUrl+'place/user/'+post.user_link_id"
                class="lyRelative lyZIndex3 pFontBold"
              >{{post.user_nickname}}</a>
            </p>
            <div class="lyHorizontal pFont12 pFontMedium">
              <span class="lyMr4">さん</span>
              <span
                v-if="post.user.public_prefecture !== ''"
                class="lyMr4"
              >{{post.user.public_prefecture}}</span>
              <span>{{decade}}代</span>
            </div>
          </div>
          <div class="lyShrinkNone lyVerticalStart">
            <p
              v-if="Number(post.emotion) === 1"
              class="lyInlineBlock lyMr4 lyPt6 lyPb6 lyPr8 lyPl8 pFont12 pFontBold pFontWhite pBackColorYellow pBorderRadiusEllipse"
            >キャンペーン</p>
            <p
              v-if="post.is_answers_wanted"
              class="lyInlineBlock lyMr4 lyPt6 lyPb6 lyPr8 lyPl8 pFont12 pFontBold pFontWhite pBackColorPink pBorderRadiusEllipse"
            >
              <a
                v-if="isDetailMode"
                :href="siteUrl+'place/list?filter=wanted'"
                style="color: #fff;font-weight: 700;"
              >回答募集</a>
              <span v-else>回答募集</span>
            </p>
            <div
              v-if="Number(loginUserId) !== 0"
              class="lyRelative lyZIndex3"
            >
              <label class="pChip dBookmark dGreen">
                <input
                  :checked="favorite"
                  type="checkbox"
                  @change="emits('toggle-favorite', $event.target.checked)"
                >
                <span class="pFont14 pFontBold">保存</span>
              </label>
            </div>
          </div>
        </div>
        <ul class="lyHorizontalBreak lyMt4">
          <ChildList
            v-for="child in props.post.user.public_children"
            :key="child.id"
            :child="child"
          />
        </ul>
      </div>
      <div class="lyOrder1 lyPd8 lyVerticalCntBreak">
        <p class="lyMr8 pFont12 pFontGray30"><time
          :datetime="createdAt.toISOString()"
          class="pFontMedium"
        >{{createdAt.getFullYear()}}.{{String(createdAt.getMonth() + 1).padStart(2, '0')}}.{{String(createdAt.getDate()).padStart(2, '0')}} {{String(createdAt.getHours()).padStart(2, '0')}}:{{String(createdAt.getMinutes()).padStart(2, '0')}}</time></p>
        <p
          v-for="tag in campaignTags"
          :key="tag.id"
        >
          <a
            :href="siteUrl+'place/tag/'+tag.id"
            class="lyVerticalCnt lyPd4 pFont12 pFontMedium pFontBlack pBackColorGray4 pBorderRadius2px"
          >
            お題
            <span class="lyMl2">#{{tag.name}}</span>
          </a>
        </p>
      </div>
      <div :class="{lyOrder3: true, lyPd8: true, lyOverFlewHidden: true, pageBoardOmission: isShowMore}">
        <template
          v-for="(paragraph, index) in contentParagraph"
        >
          <br v-if="index !== 0">
          {{paragraph}}
        </template>
        <div
          v-if="isDetailMode && supplementRemote !== ''"
          class="lyMt8"
        >
          <p class="lyInlineBlock lyPd4 lyMb8 pFont10 pFontBold pBackColorGray4">補足</p>
          <template
            v-for="(paragraph, index) in supplementRemoteParagraph"
          >
            <br v-if="index !== 0">
            <p>{{paragraph}}</p>
          </template>
        </div>
      </div>
      <p
        v-if="isShowMore"
        class="lyOrder4 lyPd5 lyTextCnt pFont12 pFontBold"
      >もっと見る</p>
      <div :class="'lyPd8 '+lyOrder4or5">
        <ul class="lyHorizontalBreak">
          <li
            v-for="category in post.categories"
            :key="category.id"
            class="lyMr8 lyMt4 lyMb4"
          >
            <a
              :href="siteUrl+'place/category/'+category.id"
              class="lyInlineBlock lyPt6 lyPb6 lyPr8 lyPl8 pFont12 pFontBold pFontWhite pBackColorGreen pBorderRadiusEllipse"
            >{{category.name}}</a>
          </li>
        </ul>
      </div>
      <div :class="'lyPd8 '+lyOrder5or6">
        <ul class="lyHorizontalBreak">
          <TagList
            v-for="tag in props.post.tags"
            :key="tag.id"
            :categories="post.categories"
            :tag="tag"
          />
        </ul>
      </div>
      <div :class="'lyPl8 '+lyOrder6or7">
        <div class="lyVerticalBwCnt">
          <div class="lyHorizontal lyRelative lyZIndex3">
            <p class="lyMr8">
              <label
                :style="Number(loginUserId) === Number(post.user.id) ? 'pointer-events:none;' : ''"
                class="pChip dHeart dRed"
              >
                <input
                  :checked="empathy"
                  type="checkbox"
                  @change="emits('toggle-empathy', $event.target.checked)"
                >
                <span class="pFont14 pFontBold">共感<span class="lyMl4 pFontMedium">{{empathyCount}}</span></span>
              </label>
            </p>
            <p>
              <a
                :href="siteUrl+'place/'+post.id+'#comment'"
                class="pChip dComment"
              >
                <span class="pFont14 pFontMedium">{{post.count_comment}}</span>
              </a>
            </p>
          </div>
          <div
            v-if="isMyPost"
            :class="{modSelectTrigger: true, mdBoardTrigger: true, isShow: isOpenMenu}"
          >
            <teleport to="body">
              <div :class="{gbModalBack: true, gbTriggerModalBack: true, isShow: isOpenMenu}"></div>
            </teleport>
            <button
              type="button"
              style="padding: 6px 8px 8px 8px;"
              @click="emits('open-menu')"
            ><img
              :src="siteUrl+'images/iconMore.svg'"
              width="16"
              height="16"
              alt="投稿の設定"
            ></button>
            <div class="modSelectListContainer mdRight">
              <ul class="modSelectList">
                <li
                  v-if="supplementRemote === ''"
                  class="pBorderBottom1pxSolidGray4"
                >
                  <button
                    v-if="isDetailMode"
                    class="lyBlock lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pFontBlack pHoverBackGray4 pAnimation pCursorPointer"
                    @click="isOpenSupplementEditor = true, emits('close-menu')"
                  >投稿に補足</button>
                  <a
                    v-else
                    :href="siteUrl+'place/'+post.id+'?supplement=1'"
                    class="lyBlock lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pFontBlack pHoverBackGray4 pAnimation pCursorPointer"
                  >投稿に補足</a>
                </li>
                <li class="pBorderBottom1pxSolidGray4">
                  <button
                    type="button"
                    class="lyBlock lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pFontBlack pHoverBackGray4 pAnimation pCursorPointer"
                    @click="emits('show-publish-modal'), emits('close-menu')"
                  >公開設定</button>
                </li>
                <li>
                  <button
                    type="button"
                    class="lyBlock lyW100 lyPt12 lyPb12 lyPr8 lyPl8 lyTextCnt pFontBold pFontBlack pHoverBackGray4 pAnimation pCursorPointer"
                    @click="emits('show-comment-setting-modal'), emits('close-menu')"
                  >みんなからのコメント設定</button>
                </li>
              </ul>
            </div>
          </div>
          <button
            v-else-if="Number(loginUserId) !== 0"
            type="button"
            class="lyPd8 lyRelative lyZIndex3"
            @click="emits('toggle-violation', ! violation)"
          ><img
            :src="siteUrl+(violation ? 'images/iconViolationCrnt.svg' : 'images/iconViolation.svg')"
            style="width: 16px;"
            width="16"
            height="16"
            loading="lazy"
            alt="違反報告はこちら"
          ></button>
        </div>
      </div>
      <a
        v-if=" ! isDetailMode"
        :href="siteUrl+'place/'+post.id"
        class="pageBoardLink"
      ></a>
      <div
        v-if="isDetailMode"
        class="lyOrder7 lyPd16 pBorderTop1pxSolidGray4 pBorderBottom1pxSolidGray4"
      >
        <ul class="lyHorizontal">
          <li class="lyMr8">
            <a
              :href="'https://twitter.com/intent/tweet?url='+encodeURIComponent(siteUrl+'place/'+post.id)"
              class="lyW74px"
            ><img
              :src="siteUrl+'images/shareX.svg'"
              width="74"
              height="25"
              loading="lazy"
              alt="この投稿をXでシェア"
            ></a>
          </li>
          <li class="lyMr8">
            <a
              :href="'https://social-plugins.line.me/lineit/share?url='+encodeURIComponent(siteUrl+'place/'+post.id)+'&amp;src=sdkpreparse'"
              class="lyW74px"
            ><img
              :src="siteUrl+'images/shareLine.svg'"
              width="74"
              height="25"
              loading="lazy"
              alt="この投稿をLINEでシェア"
            ></a>
          </li>
          <li class="lyMr8">
            <a
              :href="'https://www.facebook.com/sharer/sharer.php?u='+encodeURIComponent(siteUrl+'place/'+post.id)+'&amp;src=sdkpreparse'"
              class="lyW74px"
            ><img
              :src="siteUrl+'images/shareFacebook.svg'"
              width="74"
              height="25"
              loading="lazy"
              alt="この投稿をfacebookでシェア"
            ></a>
          </li>
          <li>
            <a
              href="#commentPost"
              class="lyW74px"
            >
              <img
              :src="siteUrl+'images/shareComment.svg'"
              width="74"
              height="25"
              loading="lazy"
              alt="コメントを書く"
            >
            </a>
          </li>
        </ul>
      </div>
      <div
        v-if="isDetailMode && isOpenSupplementEditor"
        class="lyOrder8"
      >
        <div class="lyPd8">
          <p class="lyInlineBlock lyPd4 lyMb8 pFont10 pFontBold pBackColorGray4">補足</p>
          <div v-if="isConfirm">
            <template
              v-for="(paragraph, index) in supplementLocalParagraph"
            >
              <br v-if="index !== 0">
              <p>{{paragraph}}</p>
            </template>
          </div>
          <textarea
            v-else
            v-model="supplementLocal"
            ref="supplementTextarea"
            class="lyW100"
            placeholder=" "
            rows="6"
            @change="adjustSupplementHeight"
          ></textarea>
        </div>
        <p class="lyPt8 lyPb8 lyTextCnt pFont10 pFontMedium pBackColorGray4">※氏名やメールアドレスなどの個人情報は入力しないでください。</p>
        <div class="lyHorizontal">
          <p class="lyW50 pBorderRight2pxSolidGray4">
            <button
              v-if="isConfirm"
              type="button"
              class="lyW100 lyPt16 lyPb16 pFont14 pFontBold"
              @click="isConfirm = false"
            >戻る</button>
            <button
              v-else
              type="button"
              class="lyW100 lyPt16 lyPb16 pFont14 pFontBold"
              @click="isOpenSupplementEditor = false"
            >キャンセル</button>
          </p>
          <p class="lyW50">
            <button
              v-if="isConfirm"
              type="button"
              class="lyW100 lyPt16 lyPb16 pFont14 pFontBold pBtnColorGreen"
              @click="setSupplement"
            >投稿する</button>
            <button
              v-else
              :class="{lyW100: true, lyPt16: true, lyPb16: true, pFont14: true, pFontBold: true, pFontGreen: true, isDisabled: ! validContentLength}"
              type="button"
              @click="isConfirm = true"
            >確認する</button>
          </p>
        </div>
      </div>
    </article>
  </div>
</template>
